import { useState, useEffect } from "react";
import axios from "axios";
import { Row, Col, ListGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL } from "config";
import AddCompatibleProduct from "pages/ProductCompatibility/components/Compatibility/components/CompatibleProductsList/components/AddCompatibleProduct";
import { arraysEqual } from "lib/utils";

const CompatibleProductsList = ({
  compatibleProductsBCIds,
  categoryBcId,
  baseProductId,
  compatibilityId,
  updateCompatibility,
  onAddedCompatibility,
  compatibleProductsBCIdsPositionsFromServer,
}) => {
  const [compatibleProducts, setCompatibleProducts] = useState([]);
  const [
    compatibleProductsBCIdsPositions,
    setCompatibleProductsBCIdsPositions,
  ] = useState([]);

  // This handles the scenario in which the user has not moved any row. This creates initial data.
  const getInitialCompatibleProductsBCIdsPositions = () => {
    const compatibleProductsBCIdsPositions = compatibleProductsBCIds.map(
      (compatibleProductsBCId, index) => ({
        id: compatibleProductsBCId,
        position: index,
      })
    );
    return compatibleProductsBCIdsPositions;
  };

  const getCompatibleProducts = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/bc/products?id:in=${compatibleProductsBCIds.join(
          ","
        )}`
      );
      if (response.status === 200) {
        const compatibleProducts = response.data.products;
        // adding list position to compatibleProducts received from API call to BC. Then sort list using position.
        addToStateSortedCompatibleProducts(compatibleProducts);
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const addToStateSortedCompatibleProducts = (compatibleProducts) => {
    compatibleProducts.forEach((compatibleProduct) => {
      const compatibleProductsBCIdsPosition =
        compatibleProductsBCIdsPositions.filter(
          (compatibleProductsBCIdPosition) => {
            return compatibleProductsBCIdPosition.id == compatibleProduct.id;
          }
        );
      if (typeof compatibleProductsBCIdsPosition[0]?.position === "number") {
        compatibleProduct.position =
          compatibleProductsBCIdsPosition[0]?.position;
      }
    });
    compatibleProducts = compatibleProducts.sort(
      (a, b) => a.position - b.position
    );
    setCompatibleProducts([...compatibleProducts]);
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) {
      return;
    }
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }
    reOrderCompatibleProductsBCIdsPositions(destination, source, draggableId);
  };

  const reOrderCompatibleProductsBCIdsPositions = (
    destination,
    source,
    draggableId
  ) => {
    // updating the position of moved product.
    let currentCompatibleProductsBCIdsPosition =
      compatibleProductsBCIdsPositions.filter(
        (newCompatibleProductsBCIdsPosition) =>
          newCompatibleProductsBCIdsPosition.id == draggableId
      )[0];
    currentCompatibleProductsBCIdsPosition.position = destination.index;
    // Product moved down the list, as position become smaller (ex: 3 => 0). Increasing the position of all products above the moved product by 1.
    if (destination.index < source.index) {
      let i = source.index - 1;
      do {
        let currentCompatibleProductsBCIdsPosition =
          compatibleProductsBCIdsPositions.filter(
            (newCompatibleProductsBCIdsPosition) =>
              newCompatibleProductsBCIdsPosition.position == i &&
              String(newCompatibleProductsBCIdsPosition.id) !== draggableId
          )[0];
        currentCompatibleProductsBCIdsPosition.position = i + 1;
        i = i - 1;
      } while (i > destination.index - 1);
    }
    // Product moved up the list, as position become bigger (ex: 0 => 3). Decreasing the position of all products below the moved product by 1.
    if (source.index < destination.index) {
      let i = source.index + 1;
      do {
        let currentCompatibleProductsBCIdsPosition =
          compatibleProductsBCIdsPositions.filter(
            (newCompatibleProductsBCIdsPosition) =>
              newCompatibleProductsBCIdsPosition.position == i &&
              String(newCompatibleProductsBCIdsPosition.id) !== draggableId
          )[0];
        currentCompatibleProductsBCIdsPosition.position = i - 1;
        i = i + 1;
      } while (i < destination.index + 1);
    }
    // Adding re-ordered compatibleProductsBCIdsPositions to the state.
    setCompatibleProductsBCIdsPositions([...compatibleProductsBCIdsPositions]);
    // Updating compatibleProductsBCIdsPositions key in compatibility object in the DB.
    if (compatibleProductsBCIdsPositions.length > 0) {
      updateCompatibility({
        compatibleProductsBCIdsPositions,
      });
    }
  };

  useEffect(() => {
    // If there is server data for compatibleProductsBCIdsPositions, use it.
    if (compatibleProductsBCIdsPositionsFromServer?.length > 0) {
      setCompatibleProductsBCIdsPositions([
        ...compatibleProductsBCIdsPositionsFromServer,
      ]);
    } else {
      // No server data for compatibleProductsBCIdsPositions, make data based on compatibleProductsBCIds array.
      const compatibleProductsBCIdsPositions =
        getInitialCompatibleProductsBCIdsPositions();
      setCompatibleProductsBCIdsPositions(compatibleProductsBCIdsPositions);
    }
    getCompatibleProducts(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If compatibleProductsBCIdsPositionsFromServer amount has changed, then set compatibleProductsBCIdsPositions again.
  useEffect(() => {
    if (compatibleProductsBCIdsPositionsFromServer?.length > 0) {
      setCompatibleProductsBCIdsPositions([
        ...compatibleProductsBCIdsPositionsFromServer,
      ]);
    }
  }, [compatibleProductsBCIdsPositionsFromServer?.length]);

  // If compatibleProductsBCIdsPositionsFromServer positions has changed, then set compatibleProductsBCIdsPositions again.
  useEffect(() => {
    if (
      !compatibleProductsBCIdsPositionsFromServer ||
      !compatibleProductsBCIdsPositions
    ) {
      return;
    }
    const positionsUnchanged = arraysEqual(
      compatibleProductsBCIdsPositionsFromServer,
      compatibleProductsBCIdsPositions
    );
    if (!positionsUnchanged) {
      setCompatibleProductsBCIdsPositions([
        ...compatibleProductsBCIdsPositionsFromServer,
      ]);
    }
  }, [
    compatibleProductsBCIdsPositionsFromServer,
    compatibleProductsBCIdsPositions,
  ]);

  useEffect(() => {
    getCompatibleProducts(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compatibleProductsBCIds.length, compatibleProductsBCIdsPositions]);

  useEffect(() => {
    // When compatibleProductsBCIdsPositions is updated in the state, re-sort compatibleProducts and update the compatibleProducts in the state.
    addToStateSortedCompatibleProducts(compatibleProducts);
  }, [compatibleProductsBCIdsPositions]);

  return (
    <div style={{ padding: "10px 0px" }}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="compatibleProductsList">
          {(provided) => (
            <ListGroup {...provided.droppableProps} ref={provided.innerRef}>
              {compatibleProducts.map((compatibleProduct, index) => (
                <Draggable
                  key={compatibleProduct.id}
                  draggableId={String(compatibleProduct.id)}
                  index={index}
                >
                  {(provided) => (
                    <ListGroup.Item
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <Row>
                        <Col xs={{ span: 10 }}>
                          <span
                            style={{
                              display: "block",
                            }}
                          >
                            SKU: {compatibleProduct.sku} (Unit Count:{" "}
                            {compatibleProduct.custom_fields[0]?.value})
                          </span>
                          <span
                            style={{
                              fontWeight: "bold",
                            }}
                          >
                            {compatibleProduct.name}
                          </span>
                        </Col>
                        <Col
                          xs={{ span: 2 }}
                          style={{
                            display: "flex",
                            justifyContent: "right",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faXmark}
                            size="2xl"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this product?"
                                )
                              ) {
                                let newCompatibleProductsBCIdsPositions =
                                  compatibleProductsBCIdsPositions
                                    .filter(
                                      (compatibleProductsBCIdsPosition) =>
                                        compatibleProductsBCIdsPosition.id !==
                                        compatibleProduct.id
                                    )
                                    .map(
                                      (
                                        compatibleProductsBCIdsPosition,
                                        index
                                      ) => {
                                        compatibleProductsBCIdsPosition.position =
                                          index;
                                        return compatibleProductsBCIdsPosition;
                                      }
                                    );
                                updateCompatibility({
                                  compatibleProductsBCId: compatibleProduct.id,
                                  compatibleProductsBCIdsPositions:
                                    newCompatibleProductsBCIdsPositions,
                                });
                              }
                            }}
                          />
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ListGroup>
          )}
        </Droppable>
      </DragDropContext>
      <AddCompatibleProduct
        categoryBcId={categoryBcId}
        baseProductId={baseProductId}
        compatibilityId={compatibilityId}
        compatibleProductsBCIds={compatibleProductsBCIds}
        onAddedCompatibility={onAddedCompatibility}
        compatibleProductsBCIdsPositions={compatibleProductsBCIdsPositions}
      />
    </div>
  );
};

export default CompatibleProductsList;
